// Copyright 2018 Sorama B.V.

import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { Time } from "units/Time";
import { UnitValue } from "units/Unit";
import { BrowserExportBase } from "./BrowserExportBase";
import { ExportStage } from "./Export";
import { ImageFormat, VideoFormat } from "./Format";
import { FrameGenerator } from "./IExportService";

interface MEMFSEntry {
    name: string;
    data: ArrayBuffer;
}

interface Frame<T extends string | ArrayBuffer> {
    image: T;
    time: UnitValue;
}

export class FFMPEGVideoExport extends BrowserExportBase {
    private videoFormat: VideoFormat;
    private generator: FrameGenerator;

    constructor(videoFormat: VideoFormat, generator: FrameGenerator) {
        super(1280);

        this.videoFormat = videoFormat;
        this.generator = generator;
    }

    public exportToBuffer(progressCallback?: (stage: ExportStage, progres: number) => void): Promise<ArrayBuffer> {
        throw new Error("Not implemented");
    }

    public export(fileName: string, progressUpdate?: (stage: ExportStage, progress?: number) => void): Promise<void> {
        // Already start fetching the worker
        const workerPromise = this.initializeFFMPEGWorker();

        // Setup arguments and inputs
        if (progressUpdate) {
            progressUpdate(ExportStage.Generating);
        }

        return this.generateFrames().then((frames) => {
            const args = this.generateFFMPEGArguments(frames, this.playbackRate);
            const mem = this.generateFFMPEGMEMFS(frames);

            return workerPromise
                .then((worker) => {
                    progressUpdate(ExportStage.Encoding, 0);

                    return new Promise<void>((resolve, reject) => {
                        let stdout = "";

                        const regex = /frame=\s*([0-9]*)/g;

                        worker.onerror = (e) => {
                            console.error(e);
                            reject(e);
                        };

                        try {
                            // Execute ffmpeg
                            worker.onmessage = (e) => {
                                const msg = e.data;
                                switch (msg.type) {
                                    case "stdout":
                                    case "stderr": {
                                        // Somehow all stdout output is outputed in stderr.. attempt to parse what frame ffmpeg is processing atm.
                                        let match = regex.exec(<string>msg.data);
                                        let lastMatch: RegExpExecArray;
                                        while (match) {
                                            lastMatch = match;
                                            match = regex.exec(<string>msg.data);
                                        }

                                        if (lastMatch && progressUpdate) {
                                            try {
                                                const frameNumber = Number(lastMatch[1]);
                                                progressUpdate(ExportStage.Encoding, frameNumber / (frames.length - 1));
                                            } catch (e) {
                                                console.error(e);
                                            }
                                        }

                                        stdout += msg.data + "\n";
                                        break;
                                    }
                                    case "done": {
                                        FileDownloader.downloadBlobAsFile(
                                            new Blob([msg.data.MEMFS[0].data]),
                                            "video/" + this.videoFormat.name.toLowerCase(),
                                            fileName + "." + this.videoFormat.name.toLowerCase()
                                        );
                                        worker.terminate();
                                        resolve();
                                        break;
                                    }
                                    case "exit": {
                                        if (e.data.data !== 0) {
                                            // Error occurred, log to output
                                            console.log(stdout);
                                            worker.terminate();
                                            reject("Unable to export video");
                                        }
                                        break;
                                    }
                                }
                            };

                            worker.postMessage({ type: "run", arguments: args, MEMFS: mem, TOTAL_MEMORY: 500000000 });
                        } catch (e) {
                            reject(e);
                        }
                    });
                })
                .catch((e) => {
                    console.log(e);
                });
        });
    }

    private initializeFFMPEGWorker(): Promise<Worker> {
        let ffmpegWorkerExePromise: Promise<unknown>;

        switch (this.videoFormat.name) {
            case VideoFormat.mp4.name:
                ffmpegWorkerExePromise = import("ffmpeg.js/ffmpeg-worker-mp4.js");
                break;
            case VideoFormat.webm.name:
                ffmpegWorkerExePromise = import("ffmpeg.js/ffmpeg-worker-webm.js");
                break;
            default:
                throw new Error("Unable to export, invalid video format");
        }

        // Load ffmpeg worker script
        return ffmpegWorkerExePromise.then((ffmpegWorkerExe) => {
            // The return type of the ffmpegWorkerExePromise should be whatever 'import' returns
            return new Worker(
                window.URL.createObjectURL(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    new Blob([(ffmpegWorkerExe as any).default], { type: "application/javascript" })
                )
            );
        });
    }

    private generateFFMPEGArguments(frames: Frame<ArrayBuffer>[], playRate = 1): string[] {
        // Find the framerate
        let frameTime = undefined;
        for (let i = 0; i < frames.length - 1; i++) {
            const fTime = frames[i + 1].time.toUnit(Time.second) - frames[i].time.toUnit(Time.second);
            if (frameTime === undefined) {
                frameTime = fTime;
            }

            if (Math.abs(fTime - frameTime / (i + 1)) > 1e-3) {
                // Frametime variation to high
                throw new Error("Unable to export, video has non-uniform framerate");
            }
            frameTime += fTime;
        }

        frameTime /= frames.length;
        frameTime /= playRate;

        const ffmpegArguments: string[] = [];
        ffmpegArguments.push("-framerate", (1.0 / frameTime).toFixed(0), "-i", "f%08d.jpg", "-c:v");

        switch (this.videoFormat.name) {
            case VideoFormat.mp4.name:
                ffmpegArguments.push(
                    "libx264",
                    "-pix_fmt",
                    "yuv420p",
                    "-profile:v",
                    "high",
                    "-level",
                    "3.2",
                    "-preset",
                    "faster",
                    "-tune",
                    "zerolatency",
                    "-crf",
                    "22",
                    "out.mp4"
                );
                break;
            case VideoFormat.webm.name:
                ffmpegArguments.push("libvpx", "-crf", "9", "-b:v", "2M", "out.webm");
                break;
            default:
                throw new Error("Unable to export, invalid video format");
        }

        return ffmpegArguments;
    }

    private generateFFMPEGMEMFS(frames: Frame<ArrayBuffer>[]): MEMFSEntry[] {
        return frames.map((x, i) => {
            const fileName = "f" + i.toString().padStart(8, "0") + ".jpg";

            return {
                name: fileName,
                data: x.image,
            };
        });
    }

    private generateFrames(): Promise<Frame<ArrayBuffer>[]> {
        const memfsPromises: Promise<Frame<ArrayBuffer>>[] = [];

        const generateAction = (): Promise<void> => {
            if (this.generator.finalFrame) return;

            const frameTime = this.generator.frameTime;
            const framePromise = this.generator.getFrame().then((x) => {
                return <Frame<string>>{
                    image: x,
                    time: frameTime,
                };
            });

            memfsPromises.push(
                framePromise.then((frame) =>
                    super
                        .rasterize(frame.image, ImageFormat.jpeg)
                        .then((rasterized) => ({ image: rasterized, time: frame.time }))
                )
            );

            return framePromise.then(() => generateAction());
        };

        return generateAction().then(() => Promise.all(memfsPromises));
    }
}
